// src/components/layout/EntityListPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Pagination,
  alpha,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  ViewModule as GridViewIcon,
  ViewList as TableViewIcon
} from '@mui/icons-material';

// Import components
import FilterBar from '../core/FilterBar';
import EntityEmptyState from '../core/EntityEmptyState';
import { Button } from '../../design-system/components/core';
import { Card } from '../../design-system/components/core';
import { EmptyState } from '../../design-system/components/core';
import { useThemeTokens, useGradientText } from '../../theme/themeHooks';

/**
 * Higher-Order Component for entity listing pages with unified logic
 */
const EntityListPage = ({
  title,
  description,
  entities = [],
  isLoading = false,
  error = null,
  pagination = {},
  filters = {},
  sort = {},
  renderEntity,
  actions = {},
  emptyState = {},
  view = {
    current: 'grid',
    available: ['grid', 'table'],
    onChange: () => {},
    content: null
  },
  ...rest
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getCardStyle, getButtonStyle } = useThemeTokens();
  const { style: gradientTextStyle } = useGradientText();
  
  // Extract props
  const { 
    page = 1, 
    pageSize = 10, 
    totalPages = 1, 
    onPageChange 
  } = pagination;
  
  const {
    searchTerm = '',
    filterValues = {},
    onSearchChange,
    onClearSearch,
    onFilterChange,
    onClearAllFilters,
    filterConfig = [],
    activeFilters = []
  } = filters;
  
  const {
    field: sortField = 'name',
    direction: sortDirection = 'asc',
    onSortChange,
    sortOptions = []
  } = sort;
  
  const {
    canCreate = false,
    createPath,
    createLabel = 'Create',
    onCreate,
    extraActions = null
  } = actions;
  
  // Handle create action
  const handleCreate = () => {
    if (onCreate) {
      onCreate();
    } else if (createPath) {
      navigate(createPath);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null && view.onChange) {
      view.onChange(newViewMode);
    }
  };
  
  // Check if filtered
  const isFiltered = () => {
    return searchTerm || 
      activeFilters.length > 0 || 
      Object.values(filterValues).some(value => value !== 'all');
  };

  // Render empty state
  const renderEmptyState = () => {
    const {
      entityType = 'generic',
      title: emptyTitle,
      description: emptyDescription,
      icon,
      getFilteredTitle,
      getFilteredDescription
    } = emptyState;

    // Check if filtering is active
    const filtered = isFiltered();

    return (
      <EntityEmptyState
        entityType={entityType}
        filtered={filtered}
        onClearFilters={onClearAllFilters}
        canCreate={canCreate}
        onCreate={handleCreate}
        createLabel={createLabel}
        customContent={{
          title: emptyTitle,
          description: emptyDescription,
          filteredTitle: getFilteredTitle ? getFilteredTitle() : undefined,
          filteredDescription: getFilteredDescription ? getFilteredDescription() : undefined,
        }}
        customIcon={icon}
        magical={true}
      />
    );
  };
  
  // Handle loading state
  if (isLoading && entities.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '70vh',
          background: theme.aghrba.gradients.pageBackground,
        }}
      >
        <EmptyState
          variant="loading"
          title={`Loading ${title ? title.toLowerCase() : 'data'}...`}
          magical={true}
        />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: theme.spacing(4) }}>
      {/* Header and Actions */}
      <Box 
        sx={{ 
          mb: theme.spacing(5), 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: theme.spacing(3),
          px: theme.spacing(3),
          py: theme.spacing(3),
          background: theme.aghrba.gradients.pageBackground,
          // borderRadius: theme.aghrba.tokens.borderRadius.medium,
        }}
      >
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              ...gradientTextStyle,
              fontFamily: theme.aghrba.typography.heading.primary,
              marginBottom: theme.spacing(1),
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{
                maxWidth: '600px',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          {/* View toggle - only if multiple views are available */}
          {view.available && view.available.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
              <ToggleButtonGroup
                value={view.current}
                exclusive
                onChange={handleViewModeChange}
                aria-label="view mode"
                size="small"
                sx={{
                  height: 40,
                  '& .MuiToggleButton-root': {
                    height: 40,
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.text.primary,
                    transition: theme.aghrba.tokens.transitions.standard,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      transform: 'none', // Prevent transform on toggle buttons
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.dark,
                      }
                    }
                  }
                }}
              >
                {view.available.includes('grid') && (
                  <ToggleButton value="grid" aria-label="grid view">
                    <Tooltip title="Grid View">
                      <GridViewIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                )}
                {view.available.includes('table') && (
                  <ToggleButton value="table" aria-label="table view">
                    <Tooltip title="Table View">
                      <TableViewIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Extra Actions - Render before the Create button */}
          {extraActions}
          
          {canCreate && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              height: 40,
              position: 'relative',
              zIndex: 1,
            }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                className="create-button"
                sx={{
                  height: 40,
                  padding: theme.spacing(0, 3),
                  fontWeight: 600,
                }}
              >
                {createLabel}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      
      {/* FilterBar */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        onClearSearch={onClearSearch}
        searchPlaceholder={`Search ${title ? title.toLowerCase() : 'items'}...`}
        filters={filterValues}
        onFilterChange={onFilterChange}
        filterConfig={filterConfig}
        onClearAllFilters={onClearAllFilters}
        sort={{ field: sortField, direction: sortDirection }}
        onSortChange={onSortChange}
        sortOptions={sortOptions}
        builtInMenus={true}
        activeFilters={activeFilters}
        showActiveFilters={true}
        hasActiveFilters={activeFilters.length > 0}
      />
      
      
      {/* Empty State or Entity Content */}
      {entities.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          {/* Content based on view mode - from container */}
          {view.content}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: theme.spacing(6),
                mb: theme.spacing(4)
              }}
            >
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={onPageChange}
                color="secondary"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    // borderRadius: theme.aghrba.tokens.borderRadius.medium,
                    transition: theme.aghrba.tokens.transitions.standard,
                    '&:hover': {
                      backgroundColor: theme.palette.background.light,
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.dark,
                      }
                    }
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default EntityListPage;